import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { 
  Palette, Check, Sun, Moon, Sparkles, Waves, Flower2, Trees, 
  Sunset, Save, RotateCcw, Eye, Type
} from 'lucide-react';

const PRESET_ICONS = {
  cosmic_dark: Moon,
  celestial_light: Sun,
  ocean_breeze: Waves,
  rose_garden: Flower2,
  forest_spirit: Trees,
  sunset_glow: Sunset,
};

const FONT_OPTIONS = [
  { key: 'inter', name: 'Inter', description: 'Clean & modern (Twitter/X style)', preview: "'Inter', sans-serif" },
  { key: 'poppins', name: 'Poppins', description: 'Friendly & rounded (Spotify style)', preview: "'Poppins', sans-serif" },
  { key: 'dm_sans', name: 'DM Sans', description: 'Sleek & minimal (Figma style)', preview: "'DM Sans', sans-serif" },
  { key: 'space_grotesk', name: 'Space Grotesk', description: 'Futuristic & bold', preview: "'Space Grotesk', sans-serif" },
  { key: 'plus_jakarta', name: 'Plus Jakarta Sans', description: 'Modern & professional', preview: "'Plus Jakarta Sans', sans-serif" },
  { key: 'nunito', name: 'Nunito', description: 'Soft & approachable', preview: "'Nunito', sans-serif" },
  { key: 'cinzel', name: 'Cinzel', description: 'Elegant & spiritual', preview: "'Cinzel', serif" },
];

export default function AdminThemeSettings() {
  const { token } = useAuth();
  const { 
    themeSettings, 
    presetThemes, 
    currentColors,
    currentFonts,
    updateTheme, 
    previewTheme, 
    previewCustomColors,
    previewFonts,
    resetToSaved,
  } = useTheme();

  const [themeType, setThemeType] = useState('preset');
  const [selectedPreset, setSelectedPreset] = useState('cosmic_dark');
  const [customColors, setCustomColors] = useState({
    primary: '#d4af37',
    secondary: '#9b59b6',
    accent: '#00ced1',
    background: '#0a0a1a',
    backgroundSecondary: '#1a0a2e',
    text: '#f8f8ff',
  });
  const [fontHeading, setFontHeading] = useState('inter');
  const [fontBody, setFontBody] = useState('inter');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('colors');

  useEffect(() => {
    if (themeSettings) {
      setThemeType(themeSettings.theme_type || 'preset');
      setSelectedPreset(themeSettings.preset_name || 'cosmic_dark');
      setFontHeading(themeSettings.font_heading || 'inter');
      setFontBody(themeSettings.font_body || 'inter');
      if (themeSettings.custom_colors) {
        setCustomColors(prev => ({ ...prev, ...themeSettings.custom_colors }));
      }
    }
  }, [themeSettings]);

  const handlePresetSelect = (presetName) => {
    setSelectedPreset(presetName);
    setThemeType('preset');
    previewTheme(presetName);
    setHasChanges(true);
  };

  const handleCustomColorChange = (key, value) => {
    const newColors = { ...customColors, [key]: value };
    setCustomColors(newColors);
    setThemeType('custom');
    previewCustomColors(newColors);
    setHasChanges(true);
  };

  const handleFontChange = (type, fontKey) => {
    if (type === 'heading') {
      setFontHeading(fontKey);
      previewFonts(fontKey, fontBody);
    } else {
      setFontBody(fontKey);
      previewFonts(fontHeading, fontKey);
    }
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const settings = {
        theme_type: themeType,
        preset_name: themeType === 'preset' ? selectedPreset : null,
        custom_colors: themeType === 'custom' ? customColors : null,
        font_heading: fontHeading,
        font_body: fontBody,
      };
      await updateTheme(settings, token);
      setHasChanges(false);
    } catch (error) {
      alert('Failed to save theme settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    resetToSaved();
    setThemeType(themeSettings.theme_type || 'preset');
    setSelectedPreset(themeSettings.preset_name || 'cosmic_dark');
    setFontHeading(themeSettings.font_heading || 'inter');
    setFontBody(themeSettings.font_body || 'inter');
    if (themeSettings.custom_colors) {
      setCustomColors(prev => ({ ...prev, ...themeSettings.custom_colors }));
    }
    setHasChanges(false);
  };

  const colorFields = [
    { key: 'primary', label: 'Primary Color', desc: 'Main accent (buttons, highlights)' },
    { key: 'secondary', label: 'Secondary Color', desc: 'Supporting accent' },
    { key: 'accent', label: 'Accent Color', desc: 'Additional accent' },
    { key: 'background', label: 'Background', desc: 'Main page background' },
    { key: 'backgroundSecondary', label: 'Card Background', desc: 'Card/section backgrounds' },
    { key: 'text', label: 'Text Color', desc: 'Primary text color' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-theme-settings">
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Palette className="w-8 h-8" style={{ color: currentColors.primary }} />
            <div>
              <h1 className="text-2xl font-cinzel" style={{ color: currentColors.primary }}>
                Theme Settings
              </h1>
              <p style={{ color: currentColors.textMuted }}>
                Customize colors and fonts
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            {hasChanges && (
              <button
                onClick={handleReset}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border transition-all"
                style={{ borderColor: currentColors.border, color: currentColors.textMuted }}
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="cosmic-btn flex items-center gap-2"
              data-testid="save-theme-btn"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Theme
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('colors')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === 'colors' ? 'border-2' : 'opacity-60'
            }`}
            style={{ 
              backgroundColor: currentColors.cardBg,
              borderColor: activeTab === 'colors' ? currentColors.primary : 'transparent',
              color: currentColors.text
            }}
          >
            <Palette className="w-4 h-4" />
            Colors
          </button>
          <button
            onClick={() => setActiveTab('fonts')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === 'fonts' ? 'border-2' : 'opacity-60'
            }`}
            style={{ 
              backgroundColor: currentColors.cardBg,
              borderColor: activeTab === 'fonts' ? currentColors.primary : 'transparent',
              color: currentColors.text
            }}
          >
            <Type className="w-4 h-4" />
            Fonts
          </button>
        </div>
      </div>

      {activeTab === 'colors' && (
        <>
          <div className="glass-card p-6">
            <div className="flex gap-4 mb-6">
              <button
                onClick={() => setThemeType('preset')}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  themeType === 'preset' ? '' : 'opacity-60'
                }`}
                style={{ 
                  backgroundColor: currentColors.cardBg,
                  borderColor: themeType === 'preset' ? currentColors.primary : 'transparent',
                  color: currentColors.text
                }}
              >
                <Sparkles className="w-6 h-6 mx-auto mb-2" style={{ color: currentColors.primary }} />
                <p className="font-cinzel">Preset Themes</p>
                <p className="text-sm mt-1" style={{ color: currentColors.textMuted }}>
                  Beautiful pre-designed themes
                </p>
              </button>
              
              <button
                onClick={() => setThemeType('custom')}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  themeType === 'custom' ? '' : 'opacity-60'
                }`}
                style={{ 
                  backgroundColor: currentColors.cardBg,
                  borderColor: themeType === 'custom' ? currentColors.primary : 'transparent',
                  color: currentColors.text
                }}
              >
                <Palette className="w-6 h-6 mx-auto mb-2" style={{ color: currentColors.secondary }} />
                <p className="font-cinzel">Custom Colors</p>
                <p className="text-sm mt-1" style={{ color: currentColors.textMuted }}>
                  Create your own scheme
                </p>
              </button>
            </div>
          </div>

          {themeType === 'preset' && (
            <div className="glass-card p-6">
              <h2 className="text-xl font-cinzel mb-6" style={{ color: currentColors.primary }}>
                Select a Theme
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(presetThemes).map(([key, theme]) => {
                  const Icon = PRESET_ICONS[key] || Sparkles;
                  const isSelected = selectedPreset === key;
                  const colors = theme.colors;
                  
                  return (
                    <button
                      key={key}
                      onClick={() => handlePresetSelect(key)}
                      className={`relative p-4 rounded-xl border-2 transition-all hover:scale-[1.02] ${
                        isSelected ? 'ring-2 ring-offset-2' : ''
                      }`}
                      style={{
                        background: colors.background,
                        borderColor: isSelected ? colors.primary : colors.border,
                      }}
                      data-testid={`preset-${key}`}
                    >
                      {isSelected && (
                        <div 
                          className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: colors.primary }}
                        >
                          <Check className="w-4 h-4" style={{ color: colors.background }} />
                        </div>
                      )}
                      
                      <div className="flex items-center gap-3 mb-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: colors.cardBg }}
                        >
                          <Icon className="w-5 h-5" style={{ color: colors.primary }} />
                        </div>
                        <div className="text-left">
                          <p className="font-cinzel" style={{ color: colors.text }}>{theme.name}</p>
                          <p className="text-xs" style={{ color: colors.textMuted }}>{theme.description}</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-1">
                        {['primary', 'secondary', 'accent'].map(colorKey => (
                          <div
                            key={colorKey}
                            className="flex-1 h-6 rounded"
                            style={{ backgroundColor: colors[colorKey] }}
                          />
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {themeType === 'custom' && (
            <div className="glass-card p-6">
              <h2 className="text-xl font-cinzel mb-6" style={{ color: currentColors.primary }}>
                Custom Colors
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {colorFields.map(({ key, label, desc }) => (
                  <div key={key} className="space-y-2">
                    <label className="flex items-center justify-between">
                      <div>
                        <p style={{ color: currentColors.text }}>{label}</p>
                        <p className="text-xs" style={{ color: currentColors.textMuted }}>{desc}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={customColors[key]}
                          onChange={(e) => handleCustomColorChange(key, e.target.value)}
                          className="w-10 h-10 rounded cursor-pointer border-0 bg-transparent"
                        />
                        <input
                          type="text"
                          value={customColors[key]}
                          onChange={(e) => handleCustomColorChange(key, e.target.value)}
                          className="w-24 px-2 py-1 rounded text-sm font-mono"
                          style={{
                            backgroundColor: currentColors.cardBg,
                            color: currentColors.text,
                            border: `1px solid ${currentColors.border}`
                          }}
                        />
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'fonts' && (
        <div className="glass-card p-6">
          <h2 className="text-xl font-cinzel mb-6" style={{ color: currentColors.primary }}>
            Typography
          </h2>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-lg mb-4" style={{ color: currentColors.text }}>
                Heading Font
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {FONT_OPTIONS.map((font) => (
                  <button
                    key={font.key}
                    onClick={() => handleFontChange('heading', font.key)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      fontHeading === font.key ? '' : 'opacity-70'
                    }`}
                    style={{
                      backgroundColor: currentColors.cardBg,
                      borderColor: fontHeading === font.key ? currentColors.primary : currentColors.border,
                    }}
                    data-testid={`font-heading-${font.key}`}
                  >
                    <p 
                      className="text-xl mb-1" 
                      style={{ fontFamily: font.preview, color: currentColors.text }}
                    >
                      {font.name}
                    </p>
                    <p className="text-xs" style={{ color: currentColors.textMuted }}>
                      {font.description}
                    </p>
                    {fontHeading === font.key && (
                      <Check className="w-4 h-4 mt-2" style={{ color: currentColors.primary }} />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg mb-4" style={{ color: currentColors.text }}>
                Body Font
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {FONT_OPTIONS.map((font) => (
                  <button
                    key={font.key}
                    onClick={() => handleFontChange('body', font.key)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      fontBody === font.key ? '' : 'opacity-70'
                    }`}
                    style={{
                      backgroundColor: currentColors.cardBg,
                      borderColor: fontBody === font.key ? currentColors.primary : currentColors.border,
                    }}
                    data-testid={`font-body-${font.key}`}
                  >
                    <p 
                      className="text-base mb-1" 
                      style={{ fontFamily: font.preview, color: currentColors.text }}
                    >
                      The quick brown fox jumps over the lazy dog
                    </p>
                    <p className="text-xs" style={{ color: currentColors.textMuted }}>
                      {font.name} - {font.description}
                    </p>
                    {fontBody === font.key && (
                      <Check className="w-4 h-4 mt-2" style={{ color: currentColors.primary }} />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: currentColors.cardBg }}>
            <div className="flex items-center gap-2 mb-3">
              <Eye className="w-4 h-4" style={{ color: currentColors.primary }} />
              <p className="text-sm" style={{ color: currentColors.text }}>Live Preview</p>
            </div>
            <h3 
              className="text-2xl mb-2"
              style={{ fontFamily: `var(--font-heading)`, color: currentColors.text }}
            >
              Welcome to The Aura Space
            </h3>
            <p style={{ fontFamily: `var(--font-body)`, color: currentColors.textMuted }}>
              This is how your body text will look with the selected font. 
              Experience mindfulness and holistic healing through our platform.
            </p>
          </div>
        </div>
      )}

      <div 
        className="glass-card p-4 text-center"
        style={{ backgroundColor: currentColors.cardBg }}
      >
        <p className="text-sm" style={{ color: currentColors.textMuted }}>
          Changes are previewed immediately. Click "Save Theme" to apply them permanently for all users.
        </p>
      </div>
    </div>
  );
}
